import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Pencil, Trash2 } from "lucide-react";
import Chip from "../../../../Shared/ui/Chip";
import { useTranslation } from "react-i18next";

function AccountTable({ accounts, handleEditAccount, handleDeleteAccount, searchValue, ...props }) {
  const classes = useStyles();
  const { t } = useTranslation();

  // General
  const [sortedAccounts, setSortedAccounts] = useState([]);

  useEffect(() => {
    if (accounts && accounts.length > 0) {
      setSortedAccounts(accounts);
    } else {
      setSortedAccounts([]);
    }
  }, [accounts]);

  const handleSorting = (sortType) => {
    console.log("Sorting by: ", sortType);
  };

  const formatTypeCell = (isRevenue) => {
    return isRevenue ? (
      <Chip variant={"success"} size="small">
        {t('financialCenter.revenue')}
      </Chip>
    ) : (
      <Chip variant={"danger"} size="small">
        {t('financialCenter.cost')}
      </Chip>
    );
  };

  const formatActiveCell = (isActive) => {
    return isActive ? (
      <Chip variant={"success"} size="small">
        {t('common.active')}
      </Chip>
    ) : (
      <Chip variant={"default"} size="small">
        {t('common.inactive')}
      </Chip>
    );
  };

  // Function to highlight search text
  const highlightText = (text, searchValue) => {
    if (!searchValue || !text || searchValue.trim() === "") return text;

    const trimmedSearchValue = searchValue.trim();
    const regex = new RegExp(`(${trimmedSearchValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) => {
      if (part.toLowerCase() === trimmedSearchValue.toLowerCase()) {
        return (
          <mark key={index} style={{ backgroundColor: "var(--color-primary)", color: "#F8F8F8", padding: "0", borderRadius: "2px" }}>
            {part}
          </mark>
        );
      }
      return part;
    });
  };

  return (
    <React.Fragment>
      <div className={classes.tableContainer}>
        <table className={classes.table}>
          <thead className={classes.table__header}>
            <tr>
              <th onClick={() => handleSorting("name")}>{t('common.name')}</th>
              <th onClick={() => handleSorting("category")}>{t('financialCenter.category')}</th>
              <th onClick={() => handleSorting("type")}>{t('common.type')}</th>
              <th onClick={() => handleSorting("company")}>{t('user.company')}</th>
              <th onClick={() => handleSorting("active")}>{t('common.active')}</th>
              <th style={{ minWidth: "16px" }} />
            </tr>
          </thead>
          <tbody className={classes.table__body}>
            {Array.isArray(sortedAccounts) && sortedAccounts.length > 0 ? (
              sortedAccounts.map((account, index) => (
                <tr className={classes.table__row} key={index}>
                  <td>{highlightText(account.name, searchValue)}</td>
                  <td>{highlightText(account.categoryName, searchValue)}</td>
                  <td>{formatTypeCell(account.isRevenue)}</td>
                  <td>{account.companyName}</td>
                  <td>{formatActiveCell(account.isActive)}</td>
                  <td>
                    <div className={classes.buttonContainer}>
                      <button className={classes.iconButton} onClick={() => handleEditAccount(account.id)}>
                        <Pencil className={classes.iconButton__icon} />
                      </button>
                      <button className={classes.iconButton} onClick={() => handleDeleteAccount(account.id)}>
                        <Trash2 className={classes.iconButton__icon} style={{ color: "#D84949" }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" style={{ textAlign: "center", padding: "32px" }}>
                  {t('financialCenter.noAccountsFound')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </React.Fragment>
  );
}

export default AccountTable;

const useStyles = makeStyles({
  buttonContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "8px",
  },

  iconButton: {
    height: "32px",
    width: "32px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    borderRadius: "4px",
    border: "none",

    backgroundColor: "#F8F8F8",

    "&:hover": {
      backgroundColor: "#EDEDED",
    },
  },

  iconButton__icon: {
    height: "20px",
    width: "20px",

    display: "block",
  },

  tableContainer: {
    width: "100%",
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    borderSpacing: "0",

    backgroundColor: "#FFFFFF",
    borderRadius: "8px",
  },

  table__header: {
    backgroundColor: "#DFDFDF",
    borderRadius: "8px 8px 0px 0px",

    "& th": {
      height: "36px",
      padding: "0px 16px",
      textAlign: "left",
      fontSize: "16px",
      fontWeight: "normal",
      color: "#0C0E29",

      "&:first-child": {
        borderTopLeftRadius: "8px",
      },

      "&:last-child": {
        borderTopRightRadius: "8px",
        width: "0px",
        padding: "0px",
      },
    },
  },

  table__body: {
    "& tr": {
      borderBottom: "1px solid #EDEDED",

      "&:last-child": {
        borderBottom: "none",

        "& td": {
          "&:first-child": {
            borderBottomLeftRadius: "8px",
          },

          "&:last-child": {
            borderBottomRightRadius: "8px",
          },
        },
      },

      "& td": {
        height: "48px",
        padding: "0px 16px",
        textAlign: "left",
        fontSize: "16px",
        fontWeight: "normal",
        color: "#0C0E29",

        whiteSpace: "nowrap", // Prevent wrapping for all cells
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: 200, // Default max width for all cells

        borderRight: "1px solid #EDEDED",

        "&:last-child": {
          borderRight: "none",
          width: "fit-content",
          padding: "8px",
        },
      },
    },
  },

  table__row: {
    backgroundColor: "#FFFFFF !important",
  },
});
